import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import SaveAlt from "@mui/icons-material/SaveAlt";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";

import "./AdminComponents.scss";
import { BED_STATUS, statusCfg } from "../../ipd/utils/bedStatus";
import { getBeds, updateBed, deleteBed } from "../api/bedMasterApi";
import {
  dataGridSx,
  modalBoxSx,
  confirmButtonSx,
  cancelButtonSx,
} from "./bedMasterStyles";

/**
 * Bed List — the grid half of the Bed Management screen. Same shape as
 * UserDetails / DoctorList: a DataGrid with per-row Edit and Delete, both
 * driven by the bed `code`.
 *
 * Room, Ward and Room Category are read-only: GET returns them (derived from
 * the bed's room FK), but neither create nor update accepts a room, so there is
 * nothing to edit.
 *
 * `bedTypes` backs the edit modal's picker and comes from
 * BedManagement, which loads them once for this list and the add form.
 */
function BedList({ refreshList, bedTypes }) {
  const [beds, setBeds] = useState([]);

  const [codeToDelete, setCodeToDelete] = useState(null);
  const [editingBed, setEditingBed] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchBeds = () => {
    getBeds()
      .then((list) =>
        setBeds(
          // ipd_bed/ has no order_by, so rows arrive in DB order and a bed you
          // just added lands wherever the DB puts it — usually past page 1 of
          // ten, which is why a save looked like it had not worked. Sorting
          // newest first (id is the auto-increment PK) puts it on top. No row
          // is dropped; the full list is still there on the later pages.
          [...list]
            .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0))
            .map((item) => ({
              code: item.code ?? "-",
              // name / location go back out in the update payload, so they hold
              // the raw value — a "-" placeholder here would be saved as a
              // literal "-". The columns render the dash instead.
              name: item.name ?? "",
              location: item.location ?? "",
              bed_type_code: item.bed_type_code ?? "",
              room_name: item.room_name || "-",
              ward_name: item.ward_name || "-",
              room_category_name: item.room_category_name || "-",
              status: item.status || "-",
              is_active: item.is_active !== undefined ? item.is_active : true,
            })),
        ),
      )
      .catch((err) => {
        console.error("getBeds:", err);
        toast.error("Could not load beds");
      });
  };

  useEffect(() => {
    fetchBeds();
  }, [refreshList]);

  const handleConfirmDelete = () => {
    const code = codeToDelete;
    deleteBed(code)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Bed Deleted Successfully");
          // Same as BedTypeList: soft delete + GET filters is_active=True, so
          // dropping the row locally matches what a refetch would return.
          setBeds((rows) => rows.filter((row) => row.code !== code));
        } else {
          toast.error(res?.message || "Could not delete bed");
        }
      })
      .catch((err) => {
        console.error("deleteBed:", err);
        toast.error(err?.response?.data?.message || "Could not delete bed");
      })
      .finally(() => setCodeToDelete(null));
  };

  const handleEditSubmit = () => {
    setIsSaving(true);
    // room_code is not in the update contract — the bed's room is not settable
    // through this endpoint.
    const payload = {
      code: editingBed.code,
      name: editingBed.name,
      bed_type_code: editingBed.bed_type_code,
      location: editingBed.location,
      is_active: editingBed.is_active,
    };

    // `status` is only sent when it actually changed. The backend gates on the
    // key being present, not on the value differing — sending an unchanged
    // status rejects the whole save for anyone without the `bed_dashboard`
    // permission, so renaming a bed would fail too. `beds` still holds the
    // untouched row (editingBed is a copy), so it is the before-value.
    const original = beds.find((row) => row.code === editingBed.code);
    if (editingBed.status !== original?.status) {
      payload.status = editingBed.status;
    }

    updateBed(payload)
      .then((res) => {
        if (res?.status === "Success") {
          toast.success(res?.message || "Bed Updated Successfully");
          setEditingBed(null);
          fetchBeds();
        } else {
          toast.error(res?.message || "Could not update bed");
        }
      })
      .catch((err) => {
        console.error("updateBed:", err);
        toast.error(err?.response?.data?.message || "Could not update bed");
      })
      .finally(() => setIsSaving(false));
  };

  const columns = [
    {
      field: "name",
      headerName: "Bed Name",
      width: 160,
      valueFormatter: (params) => params?.value || "-",
    },
    { field: "code", headerName: "Bed Code", width: 120 },
    { field: "bed_type_code", headerName: "Bed Type", width: 110 },
    { field: "room_name", headerName: "Room", width: 150 },
    { field: "ward_name", headerName: "Ward", width: 150 },
    { field: "room_category_name", headerName: "Room Category", width: 150 },
    {
      field: "location",
      headerName: "Location",
      width: 140,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      field: "status",
      headerName: "Bed Status",
      width: 130,
      valueFormatter: (params) => statusCfg(params?.value).label,
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <div
          style={{ color: params.value ? "green" : "red", fontWeight: "bold" }}
        >
          {params.value ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <button
            onClick={() => setEditingBed({ ...params.row })}
            style={{ color: "blue", fontWeight: "bold", marginRight: "8px" }}
          >
            Edit
          </button>
          <button
            onClick={() => setCodeToDelete(params.row.code)}
            style={{ color: "red", fontWeight: "bold" }}
          >
            Delete
          </button>
        </>
      ),
    },
  ];

  const generatePdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.autoTable({
      head: [
        [
          "Bed Name",
          "Bed Code",
          "Bed Type",
          "Room",
          "Ward",
          "Room Category",
          "Location",
          "Bed Status",
          "Status",
        ],
      ],
      body: beds.map((b) => [
        b.name || "-",
        b.code,
        b.bed_type_code,
        b.room_name,
        b.ward_name,
        b.room_category_name,
        b.location || "-",
        statusCfg(b.status).label,
        b.is_active ? "Active" : "Inactive",
      ]),
    });
    doc.save("Bed_List.pdf");
  };

  const generateCsvData = () =>
    beds.map((b) => ({
      "Bed Name": b.name || "-",
      "Bed Code": b.code,
      "Bed Type": b.bed_type_code,
      Room: b.room_name,
      Ward: b.ward_name,
      "Room Category": b.room_category_name,
      Location: b.location || "-",
      "Bed Status": statusCfg(b.status).label,
      Status: b.is_active ? "Active" : "Inactive",
    }));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "auto",
        width: "100%",
        maxWidth: "1200px",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%" }}>
        <h4 style={{ textAlign: "center" }}>Bed List</h4>

        <div className="display-flex">
          <DataGrid
            rows={beds}
            columns={columns}
            getRowId={(row) => row.code}
            components={{
              Toolbar: () => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    sx={{ display: "flex", gap: 1 }}
                  >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "center", sm: "flex-end" },
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={generatePdf}
                      startIcon={<SaveAlt />}
                    >
                      Download PDF
                    </Button>
                    <CSVLink
                      data={generateCsvData()}
                      filename={"bed_list.csv"}
                      target="_blank"
                    >
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<SaveAlt />}
                      >
                        Download CSV
                      </Button>
                    </CSVLink>
                  </Grid>
                </Grid>
              ),
            }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10]}
            getRowClassName={() => "custom-row"}
            sx={dataGridSx}
          />
        </div>
      </div>

      <Modal
        open={!!codeToDelete}
        onClose={() => setCodeToDelete(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        aria-labelledby="confirm-delete-bed-modal"
      >
        <Box sx={modalBoxSx}>
          <h3
            id="confirm-delete-bed-modal"
            style={{ marginTop: 0, fontSize: "32px" }}
          >
            Confirm Deletion
          </h3>
          <p style={{ fontSize: "20px" }}>
            Are you sure you want to delete bed <b>{codeToDelete}</b>?
          </p>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
              mt: "10%",
            }}
          >
            <Button onClick={() => setCodeToDelete(null)} sx={cancelButtonSx}>
              CANCEL
            </Button>
            <Button onClick={handleConfirmDelete} sx={confirmButtonSx}>
              CONFIRM DELETION
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={!!editingBed}
        onClose={() => setEditingBed(null)}
        style={{
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
        aria-labelledby="edit-bed-modal"
      >
        <Box sx={{ ...modalBoxSx, maxWidth: "560px" }}>
          <h3 id="edit-bed-modal" style={{ marginTop: 0 }}>
            Edit Bed
          </h3>
          {editingBed && (
            <form>
              {/* Code identifies the bed on update — shown for context, never
                  edited. */}
              <TextField
                label="Bed Code"
                value={editingBed.code}
                disabled
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              <TextField
                label="Bed Name"
                value={editingBed.name}
                onChange={(e) =>
                  setEditingBed({ ...editingBed, name: e.target.value })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              {/* Changing the bed type re-points the billing bed to that type's
                  ward/room and updates its daily charge. */}
              <TextField
                select
                label="Bed Type"
                value={editingBed.bed_type_code}
                onChange={(e) =>
                  setEditingBed({
                    ...editingBed,
                    bed_type_code: e.target.value,
                  })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              >
                {bedTypes.map((bt) => (
                  <MenuItem key={bt.code} value={bt.code}>
                    {bt.name ? `${bt.name} (${bt.code})` : bt.code}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Location"
                value={editingBed.location}
                onChange={(e) =>
                  setEditingBed({ ...editingBed, location: e.target.value })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              />
              {/* Status changes need the bed_dashboard permission and are
                  rejected while an active admission occupies the bed — the
                  backend returns the reason, which is toasted on failure. */}
              <TextField
                select
                label="Bed Status"
                value={editingBed.status}
                onChange={(e) =>
                  setEditingBed({ ...editingBed, status: e.target.value })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              >
                {Object.entries(BED_STATUS).map(([value, cfg]) => (
                  <MenuItem key={value} value={value}>
                    {cfg.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Status"
                value={editingBed.is_active ? "true" : "false"}
                onChange={(e) =>
                  setEditingBed({
                    ...editingBed,
                    is_active: e.target.value === "true",
                  })
                }
                fullWidth
                style={{ marginBottom: "15px" }}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
              <Button
                type="submit"
                disabled={isSaving}
                onClick={(e) => {
                  e.preventDefault();
                  handleEditSubmit();
                }}
                variant="contained"
                style={{
                  backgroundColor: "#213360",
                  color: "#fff",
                  border: "2px solid #213360",
                  borderRadius: "20px",
                  marginTop: "10px",
                }}
              >
                Save Changes
              </Button>
            </form>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default BedList;
